/**
 * O erro do Portal, tipado por SITUAÇÃO — não por texto (lote P2 · achado PORTAL-04).
 *
 * A versão anterior transformava toda falha num `Error` com a mensagem do servidor, e a
 * tela mostrava essa frase num cartão vermelho. Três coisas diferentes ficavam iguais:
 *
 *   · a sessão morreu (401) — que NÃO é um cartão de erro, é voltar ao login;
 *   · o acesso ao portal mudou (403) — que é "seu acesso mudou", não "tente de novo";
 *   · a API respondeu 200 com um corpo fora do contrato — que a tela nem detectava.
 *
 * Com o tipo, cada uma tem o seu desfecho, e o desfecho é decidido onde deve ser: no
 * componente, olhando um valor, e não numa comparação de string de mensagem.
 */
export type TipoDeFalha =
  /** 401 — a sessão acabou. Volta ao login, sem shell. */
  | 'sessao'
  /** 403 — a sessão vale, o acesso a esta visão do portal não. */
  | 'proibido'
  /**
   * 409 `CONTEXTO_MUDOU` — o recorte que a tela assumiu já não é o da sessão (D-131): outra aba
   * escolheu outra empresa ou outro portal, ou a escolhida saiu do portal. Não é erro de bloco:
   * a casca relê a sessão e remonta a tela no recorte novo.
   */
  | 'contexto'
  /** 404 — o recurso não existe (ou não é seu, que aqui dá no mesmo). */
  | 'ausente'
  /** 429 — o limite de tentativas pegou. */
  | 'excesso'
  /**
   * 400/422 — o pedido foi recusado, e o servidor DISSE por quê.
   *
   * Ele existia embutido em `servidor` e isso era uma mentira útil demais para
   * durar: um link de redefinição vencido e o banco fora do ar chegavam à tela com
   * o mesmo tipo, e qualquer componente que quisesse tratar o primeiro trataria
   * também o segundo — anunciando "link vencido" para uma pane. Aqui a mensagem do
   * servidor é a que vale; nos outros tipos ela não é.
   */
  | 'requisicao'
  /** 5xx — o servidor falhou. */
  | 'servidor'
  /** 2xx com corpo que o contrato recusa. É defeito NOSSO, e não some em silêncio. */
  | 'contrato'
  /** Não houve resposta: rede, DNS, o navegador cancelou. */
  | 'rede'

export class FalhaDoPortal extends Error {
  constructor(
    readonly tipo: TipoDeFalha,
    mensagem: string,
    readonly detalhe?: string,
  ) {
    super(mensagem)
    this.name = 'FalhaDoPortal'
  }
}

/** O que a tela mostra. Curto, sem jargão e sem número de status. */
export function mensagemDe(tipo: TipoDeFalha): string {
  switch (tipo) {
    case 'sessao':
      return 'Sua sessão expirou.'
    case 'proibido':
      return 'Seu acesso a este portal mudou.'
    case 'contexto':
      // Só aparece num ato de um clique (o download): a tela já está remontando no recorte novo.
      return 'O portal ou a empresa exibida mudou. Tente de novo.'
    case 'ausente':
      return 'Não encontramos este item.'
    case 'excesso':
      return 'Muitas tentativas seguidas. Espere um instante.'
    case 'requisicao':
      // Genérica de propósito: quem chama tem a mensagem do servidor, que é melhor.
      return 'Não foi possível concluir.'
    case 'servidor':
      return 'Não foi possível carregar agora.'
    case 'contrato':
      // A pessoa não tem o que fazer com "o campo `saldoAberto` faltou". O detalhe vai
      // para o console, onde quem mantém o sistema o encontra.
      return 'Não foi possível carregar agora.'
    case 'rede':
      return 'Sem conexão com o servidor.'
  }
}
